import type { Payment } from '../../api/client';

/**
 * Yandex Metrika goals.
 *
 * The counter itself (the `tag.js` snippet) must be installed in index.html.
 * This module only sends goals through the global `ym()` function and makes
 * sure the "payment succeeded" goal is delivered at most once per payment.
 */

declare global {
  interface ImportMetaEnv {
    readonly VITE_YM_COUNTER_ID?: string;
    readonly VITE_YM_GOAL_PAYMENT_SUCCESS?: string;
  }

  interface Window {
    ym?: (
      counterId: number,
      method: 'reachGoal',
      target: string,
      params?: Record<string, unknown>,
      callback?: () => void,
    ) => void;
  }
}

/** localStorage key: `{ [paymentId]: ISO timestamp of when the goal was sent }`. */
export const SENT_PAYMENT_GOALS_KEY = 'ym_sent_payment_goals';

/** Keeps the stored map from growing forever; oldest entries are dropped first. */
const MAX_STORED_PAYMENTS = 100;

const DEFAULT_PAYMENT_SUCCESS_GOAL = 'payment_success';

/**
 * Fallback for browsers where localStorage is unavailable or throws
 * (Safari private mode, blocked storage). It still protects against
 * duplicates within the lifetime of the page (e.g. React StrictMode
 * running an effect twice).
 */
const inMemorySent = new Set<string>();

type SentMap = Record<string, string>;

function readSent(): SentMap {
  try {
    const raw = localStorage.getItem(SENT_PAYMENT_GOALS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as SentMap)
      : {};
  } catch {
    return {};
  }
}

function writeSent(map: SentMap): void {
  try {
    const entries = Object.entries(map);
    const trimmed =
      entries.length > MAX_STORED_PAYMENTS
        ? Object.fromEntries(
            entries
              .sort(([, a], [, b]) => a.localeCompare(b))
              .slice(-MAX_STORED_PAYMENTS),
          )
        : map;
    localStorage.setItem(SENT_PAYMENT_GOALS_KEY, JSON.stringify(trimmed));
  } catch {
    // Quota exceeded / storage disabled: the in-memory set still guards us.
  }
}

export function isPaymentGoalSent(paymentId: string): boolean {
  return inMemorySent.has(paymentId) || paymentId in readSent();
}

function markPaymentGoalSent(paymentId: string): void {
  inMemorySent.add(paymentId);
  writeSent({ ...readSent(), [paymentId]: new Date().toISOString() });
}

function getCounterId(): number | null {
  const raw = import.meta.env?.VITE_YM_COUNTER_ID;
  const id = raw ? Number(raw) : NaN;
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Sends the "successful payment" goal for a CONFIRMED payment exactly once
 * per payment id, even across page reloads and repeated returns from the
 * payment provider.
 *
 * Returns `true` if the goal was sent by this call, `false` if it was
 * skipped (wrong status, already sent, or Metrika is not available).
 *
 * The payment is marked as sent *before* calling `ym()` and synchronously
 * (no `await` between the check and the write), so two concurrent calls in
 * the same tab can never both pass the check.
 */
export function trackPaymentSuccess(payment: Payment): boolean {
  if (payment.status !== 'CONFIRMED') {
    console.warn(
      `[metrika] goal skipped: payment ${payment.id} status is ${payment.status}, expected CONFIRMED`,
    );
    return false;
  }
  if (isPaymentGoalSent(payment.id)) {
    console.info(`[metrika] goal skipped: payment ${payment.id} already sent`);
    return false;
  }

  const counterId = getCounterId();
  if (counterId === null) {
    console.warn(
      '[metrika] goal skipped: VITE_YM_COUNTER_ID is missing or invalid (restart the dev server after editing .env)',
    );
    return false;
  }
  // If Metrika is not loaded (missing snippet, ad blocker), do NOT mark as
  // sent: we haven't sent anything.
  if (typeof window.ym !== 'function') {
    console.warn(
      '[metrika] goal skipped: window.ym is not defined (is the counter snippet in index.html? is it blocked?)',
    );
    return false;
  }

  const goal =
    import.meta.env?.VITE_YM_GOAL_PAYMENT_SUCCESS ??
    DEFAULT_PAYMENT_SUCCESS_GOAL;

  markPaymentGoalSent(payment.id);

  try {
    window.ym(counterId, 'reachGoal', goal);
  } catch (error) {
    // Analytics must never break the payment flow.
    console.error('Metrika goal failed', error);
  }
  console.info(`[metrika] goal "${goal}" sent for payment ${payment.id}`);
  return true;
}
